import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { parse as dateFnsParse, isValid as isValidDate } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Time-of-day greeting for the current (local) time.
 * Morning < 12:00, afternoon < 18:00, otherwise evening.
 */
export function getGreeting(date: Date = new Date()): string {
  const hour = date.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

export function formatCurrency(value: number, currency: string = 'USD'): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value)
  } catch {
    // Intl throws on invalid/junk ISO codes (e.g. legacy '000'). Fall back
    // to a plain numeric format with the code appended so nothing blows up.
    return `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${currency}`
  }
}

/**
 * Returns the current fiscal year-to-date range as YYYY-MM-DD strings.
 *
 * GRM's fiscal year runs March 1 of year N → end of February of year N+1.
 * If today is January or February, we're still in FY of (currentYear - 1).
 *
 * Same logic as backend/src/utils/dates.js getFiscalYearRange() and the
 * forecasted_revenue calc in backend/src/routes/users.js.
 */
export function getFiscalYearRange(today: Date = new Date()): {
  startDate: string
  endDate: string
} {
  const fyStartYear = today.getMonth() >= 2 ? today.getFullYear() : today.getFullYear() - 1
  const start = new Date(fyStartYear, 2, 1) // March 1
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { startDate: fmt(start), endDate: fmt(today) }
}

/**
 * Returns the FULL current fiscal year as YYYY-MM-DD strings: Mar 1 of the
 * current FY → Feb 28/29 of the next year. Useful for filters like
 * "opportunities closing this year" where the user wants to include future
 * dates within the fiscal year, not just YTD.
 */
export function getFullFiscalYearRange(today: Date = new Date()): {
  startDate: string
  endDate: string
} {
  const fyStartYear = today.getMonth() >= 2 ? today.getFullYear() : today.getFullYear() - 1
  const start = new Date(fyStartYear, 2, 1) // March 1 of FY
  // Last day of February next year. `new Date(Y, 2, 0)` is "day 0 of March"
  // which JS normalises to the last day of February (28 or 29 for leap years).
  const end = new Date(fyStartYear + 1, 2, 0)
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { startDate: fmt(start), endDate: fmt(end) }
}

/**
 * Returns the full current CALENDAR year as YYYY-MM-DD strings: Jan 1 – Dec 31.
 * Counterpart to getFullFiscalYearRange for the "This Calendar Year" filter.
 */
export function getCalendarYearRange(today: Date = new Date()): {
  startDate: string
  endDate: string
} {
  const y = today.getFullYear()
  return { startDate: `${y}-01-01`, endDate: `${y}-12-31` }
}

/**
 * Parse a value for DATE-ONLY display without timezone drift.
 *
 * Postgres DATE columns (close_date, contract dates, due dates, …) serialize to
 * a UTC-midnight instant (e.g. "2026-06-01T00:00:00.000Z"); rendering that with
 * the viewer's local timezone shows the *previous* day in the Americas. For a
 * pure date string or an exact-UTC-midnight instant we rebuild the Date from the
 * literal Y-M-D components (no timezone conversion) so it renders as the stored
 * calendar date everywhere. Real timestamps (any non-midnight time) are returned
 * as-is for normal local rendering.
 */
export function toDisplayDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null
  if (date instanceof Date) return isNaN(date.getTime()) ? null : date
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]00:00:00(?:\.000)?Z?)?$/.exec(date)
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(date)
  return isNaN(d.getTime()) ? null : d
}

export function formatDate(date: string | Date | null | undefined): string {
  const parsedDate = toDisplayDate(date)
  if (!parsedDate) return '-'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsedDate)
}

export function formatDateTime(date: string | Date | null | undefined): string {
  const parsedDate = toDisplayDate(date)
  if (!parsedDate) return '-'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(parsedDate)
}

/** Local midnight today — anchor for date-only comparisons (overdue/due). */
export function startOfToday(): Date {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate())
}

/**
 * True if the value's calendar date is strictly before today (local time).
 * Safe for both date-only 'YYYY-MM-DD' values and real timestamps (today at any
 * time is NOT past; any earlier calendar day is). Use for overdue/due checks
 * instead of raw `new Date(x) < new Date()`, which is off-by-one in non-UTC zones.
 */
export function isPastDate(value: string | Date | null | undefined): boolean {
  const d = toDisplayDate(value)
  return d != null && d < startOfToday()
}

/**
 * Format a value as 'YYYY-MM-DD' for an <input type="date">, using LOCAL date
 * components. Never use `new Date(x).toISOString().split('T')[0]` for this — it
 * converts to UTC and can shift the day across timezones.
 */
export function toDateInputValue(value: string | Date | null | undefined): string {
  const d = toDisplayDate(value)
  if (!d) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Parse free-typed / pasted date text into a Date (LOCAL, no timezone shift), or
 * null if it can't be understood. Accepts common en-US formats. Used by the
 * reusable DatePicker so users can type or paste a date instead of only clicking
 * a calendar. Pair with toDateInputValue() to emit the stored 'YYYY-MM-DD'.
 */
export function parseDateInput(text: string | null | undefined): Date | null {
  if (!text) return null
  const trimmed = String(text).trim()
  if (!trimmed) return null
  // ISO / exact-midnight values go through the timezone-safe path first.
  const iso = toDisplayDate(trimmed)
  if (iso) return iso
  const formats = [
    'yyyy-MM-dd',
    'yyyy/MM/dd',
    'MM/dd/yyyy',
    'M/d/yyyy',
    'MM-dd-yyyy',
    'M-d-yyyy',
    'MMM d, yyyy',
    'MMMM d, yyyy',
    'd MMM yyyy',
    'd MMMM yyyy',
    'MMM d yyyy',
    'd MMM, yyyy',
  ]
  for (const fmt of formats) {
    const d = dateFnsParse(trimmed, fmt, new Date())
    if (isValidDate(d)) return d
  }
  return null
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value / 100)
}

// Overload signatures
export function getInitials(firstName: string, lastName?: string): string
export function getInitials(firstName?: string, lastName?: string): string

// Implementation
export function getInitials(firstName?: string, lastName?: string): string {
  if (!firstName) return ''

  // If called with a single argument that contains a space, treat it as a full name
  if (!lastName && firstName.includes(' ')) {
    const parts = firstName.trim().split(/\s+/)
    const first = parts[0]?.charAt(0) || ''
    const last = parts[parts.length - 1]?.charAt(0) || ''
    return `${first}${last}`.toUpperCase()
  }

  // Otherwise treat as first name and optional last name
  const first = firstName?.charAt(0) || ''
  const last = lastName?.charAt(0) || ''
  return `${first}${last}`.toUpperCase()
}

export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}
